import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, ShieldCheck, BookOpen, Menu, X, PlayCircle, LogOut, ChevronRight } from 'lucide-react';
import { trainingCourse, Lesson } from '../data/trainingCourse';
import { VideoPlayer } from '../components/VideoPlayer';
import { LessonResources } from '../components/training/LessonResources';
import { useScreenInit } from '../useScreenInit.js';
export function AgentPortal() {
  const screenInit = useScreenInit();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean((screenInit as {
    authenticated?: boolean;
  }).authenticated));
  const [error, setError] = useState(false);
  // Portal State
  const [activeChapterId, setActiveChapterId] = useState(trainingCourse[0].id);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'TEAMBUFF') {
      setIsAuthenticated(true);
      setError(false);
    } else {
      setError(true);
      setPassword('');
    }
  };
  const activeChapter = trainingCourse.find(c => c.id === activeChapterId) || trainingCourse[0];
  const renderLesson = (lesson: Lesson, isChild = false) => <div key={lesson.id} className={`mb-12 ${isChild ? 'ml-0 md:ml-8 mt-8 border-l-2 border-blue-100 pl-4 md:pl-8' : ''}`} data-id="element-45">
      <div className="mb-6" data-id="element-46">
        <h3 className={`${isChild ? 'text-xl' : 'text-2xl'} font-bold text-slate-900 flex items-center gap-2`} data-id="element-47">
          {!isChild && <PlayCircle className="w-6 h-6 text-blue-600 flex-shrink-0" data-id="element-48" />}
          {lesson.title}
        </h3>
        {lesson.note && <p className="text-slate-500 mt-1 font-medium ml-8" data-id="element-49">{lesson.note}</p>}
      </div>

      {/* If it has children, render the children instead of a single video for the parent */}
      {lesson.children && lesson.children.length > 0 ? <div className="space-y-2" data-id="element-50">
          {lesson.children.map(child => renderLesson(child, true))}
        </div> : <div className={!isChild ? 'ml-0 md:ml-8' : ''} data-id="element-51">
          <VideoPlayer title={lesson.title} youtubeId={lesson.youtubeId} loomId={lesson.loomId} className="w-full max-w-4xl shadow-lg ring-1 ring-slate-200" data-id="element-52" />
          {lesson.resources && lesson.resources.length > 0 && <LessonResources blocks={lesson.resources} label={lesson.resourcesLabel} data-id="element-53" />}
        </div>}
    </div>;
  if (isAuthenticated) {
    return <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row" data-id="element-54">
        {/* Mobile Header */}
        <div className="md:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between sticky top-0 z-50" data-id="element-55">
          <div className="flex items-center gap-2 text-blue-600 font-bold" data-id="element-56">
            <ShieldCheck className="w-6 h-6" data-id="element-57" />
            <span data-id="element-58">TRAINING PORTAL</span>
          </div>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-59">
            {isSidebarOpen ? <X className="w-6 h-6" data-id="element-60" /> : <Menu className="w-6 h-6" data-id="element-61" />}
          </button>
        </div>

        {/* Sidebar — always visible on md+ via CSS; toggled as a drawer on mobile */}
        <div data-id="element-62">
          {<div className={`fixed md:sticky top-0 left-0 h-screen w-72 bg-white border-r border-slate-200 shadow-2xl md:shadow-none z-40 flex-col transition-transform duration-300 md:translate-x-0 ${isSidebarOpen ? 'flex translate-x-0' : '-translate-x-full md:flex'}`} data-id="element-63">
              <div className="p-6 border-b border-slate-100 hidden md:flex items-center gap-3" data-id="element-64">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600" data-id="element-65">
                  <ShieldCheck className="w-6 h-6" data-id="element-66" />
                </div>
                <div data-id="element-67">
                  <h2 className="font-extrabold text-slate-900 leading-tight" data-id="element-68">
                    AGENT PORTAL
                  </h2>
                  <p className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-69">
                    Training Center
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-1" data-id="element-70">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-3 mt-2" data-id="element-71">
                  Course Chapters
                </p>
                {trainingCourse.map((chapter, idx) => <button key={chapter.id} onClick={() => {
              setActiveChapterId(chapter.id);
              setIsSidebarOpen(false);
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all ${activeChapterId === chapter.id ? 'bg-blue-600 text-white font-bold shadow-md shadow-blue-600/20' : 'text-slate-600 hover:bg-slate-50 font-medium'}`} data-id="element-72">
                    <span className="flex items-center gap-3" data-id="element-73">
                      <span className={`text-sm ${activeChapterId === chapter.id ? 'text-blue-200' : 'text-slate-400'}`} data-id="element-74">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      {chapter.title}
                    </span>
                    {activeChapterId === chapter.id && <ChevronRight className="w-4 h-4" data-id="element-75" />}
                  </button>)}
              </div>

              <div className="p-4 border-t border-slate-100" data-id="element-76">
                <button onClick={() => setIsAuthenticated(false)} className="w-full flex items-center gap-2 px-4 py-3 text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-colors font-medium text-sm" data-id="element-77">
                  <LogOut className="w-4 h-4" data-id="element-78" />
                  Lock Portal
                </button>
              </div>
            </div>}
        </div>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-30 md:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-79" />}

        {/* Main Content */}
        <div className="flex-1 min-w-0 bg-slate-50 overflow-y-auto h-screen" data-id="element-80">
          <div className="max-w-5xl mx-auto p-6 md:p-12" data-id="element-81">
            <motion.div key={activeChapter.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-82">
              <div className="mb-12 pb-8 border-b border-slate-200" data-id="element-83">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-84">
                  <BookOpen className="w-4 h-4" data-id="element-85" />
                  Chapter{' '}
                  {trainingCourse.findIndex(c => c.id === activeChapter.id) + 1}
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-id="element-86">
                  {activeChapter.title}
                </h1>
                <p className="text-slate-500 mt-4 text-lg font-medium" data-id="element-87">
                  {activeChapter.lessons.length} Modules • Complete all videos
                  below
                </p>
              </div>

              <div className="space-y-16" data-id="element-88">
                {activeChapter.lessons.map(lesson => renderLesson(lesson))}
              </div>

              {/* Next Chapter Button */}
              {trainingCourse.findIndex(c => c.id === activeChapter.id) < trainingCourse.length - 1 && <div className="mt-20 pt-10 border-t border-slate-200 flex justify-end" data-id="element-89">
                  <button onClick={() => {
                const nextIdx = trainingCourse.findIndex(c => c.id === activeChapter.id) + 1;
                setActiveChapterId(trainingCourse[nextIdx].id);
                const scrollContainer = document.querySelector('.overflow-y-auto.h-screen');
                if (scrollContainer) {
                  scrollContainer.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                } else {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }
              }} className="flex items-center gap-3 bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md text-slate-900 font-bold py-4 px-8 rounded-xl transition-all group" data-id="element-90">
                    Next Chapter
                    <ArrowRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" data-id="element-91" />
                  </button>
                </div>}
            </motion.div>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 relative overflow-hidden" data-id="element-92">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" data-id="element-93"></div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="max-w-md w-full relative z-10" data-id="element-94">
        <div className="text-center mb-10" data-id="element-95">
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2" data-id="element-96">
            AGENT ACCESS
          </h1>
          <p className="text-slate-600" data-id="element-97">
            Enter your team password to access training.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl" data-id="element-98">
          <div className="mb-6" data-id="element-99">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-100">
              Password
            </label>
            <div className="relative" data-id="element-101">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-102">
                <Lock className="h-5 w-5 text-slate-400" data-id="element-103" />
              </div>
              <input type="password" value={password} onChange={e => {
              setPassword(e.target.value);
              setError(false);
            }} className={`block w-full pl-11 pr-4 py-3 bg-slate-50 border ${error ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-600 focus:border-blue-600'} rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-colors`} placeholder="Enter password..." data-id="element-104" />
            </div>
            {error && <motion.p initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-red-500 text-sm mt-2" data-id="element-105">
                Incorrect password. Please try again.
              </motion.p>}
          </div>

          <button type="submit" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-md shadow-blue-600/20" data-id="element-106">
            Access Portal
            <ArrowRight className="w-5 h-5" data-id="element-107" />
          </button>
        </form>

        <div className="mt-8 text-center" data-id="element-108">
          <a href="/" className="text-slate-500 hover:text-blue-600 font-medium text-sm transition-colors" data-id="element-109">
            &larr; Back to Recruiting Page
          </a>
        </div>
      </motion.div>
    </div>;
}